import { useState, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchProdutosConsolidados, fetchFiltrosConsolidados } from '../../services/api';
import Loading from '../../components/Loading/Loading';
import Pagination from '../../components/Pagination/Pagination';
import ProductImage from '../../components/ProductImage/ProductImage';
import styles from './ProdutosConsolidados.module.css';
import tooltipStyles from '../../components/ProductTable/ProductTable.module.css';

// ─── Helpers ────────────────────────────────────────────────────────────────
// Copiar texto para a área de transferência com feedback visual
const copiarCodigo = (texto, setCopied) => {
  navigator.clipboard.writeText(texto).then(() => {
    setCopied(texto);
    setTimeout(() => setCopied(null), 1500);
  }).catch(() => {
    // Fallback para navegadores sem suporte a clipboard API
    const el = document.createElement('textarea');
    el.value = texto;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setCopied(texto);
    setTimeout(() => setCopied(null), 1500);
  });
};
const fmt = (n) => (n || 0).toLocaleString('pt-BR');
const fmtR$ = (n) => (n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const BADGE_PRESENCA = {
  centro:             { label: 'Centro',    bg: '#2563eb', icon: '🏪' },
  caxias:             { label: 'Caxias',    bg: '#7c3aed', icon: '🏬' },
  ecommerce:          { label: 'E-commerce', bg: '#059669', icon: '🛒' },
  fisico_sem_estoque: { label: 'Físico (sem estoque)', bg: '#9ca3af', icon: '📦' },
};

const ORDENACAO_OPTS = [
  { value: '',               label: 'Mais Vendidos (Total)' },
  { value: 'vendasCentro',   label: 'Mais Vendidos Centro' },
  { value: 'vendasCaxias',   label: 'Mais Vendidos Caxias' },
  { value: 'vendasEcommerce',label: 'Mais Vendidos E-commerce' },
  { value: 'nome',           label: 'Nome A→Z' },
  { value: 'codigo',         label: 'Código A→Z' },
  { value: 'preco',          label: 'Maior Preço' },
  { value: 'estoqueFisico',  label: 'Maior Estoque Físico' },
  { value: 'estoqueEcommerce', label: 'Maior Estoque E-comm' },
];

// ─── Componente de Card de Produto ──────────────────────────────────────────
const ProdutoCard = ({ produto, onImageMouseEnter, onImageMouseLeave }) => {
  const { codigo, nome, fornecedor, categoria, preco, precoCusto, imagemUrl,
          presenca, lojaFisica, ecommerce, vendasMesTotal, estoqueTotal } = produto;
  const [copiado, setCopiado] = useState(null);

  const markup = precoCusto > 0 ? ((preco - precoCusto) / precoCusto * 100) : 0;

  const presentes3 = lojaFisica.presente && ecommerce.presente;
  const somenteFisico = lojaFisica.presente && !ecommerce.presente;
  const somenteEcomm  = !lojaFisica.presente && ecommerce.presente;

  // Quando presente nos 2 canais, usar proxy com código do e-commerce (que tem imagem no cache)
  // NUNCA usar imagemUrl raw (URL S3 do Bling que expira com 403)
  const codigoEcomm = ecommerce.codigoEcommerce;
  const imagemExibir = presentes3 && codigoEcomm
    ? `/api/produtos/imagem/${codigoEcomm}`
    : `/api/produtos/imagem/${codigo}`;

  // Exibir dois códigos quando são diferentes
  const temDoisCodigos = presentes3 && codigoEcomm && codigoEcomm !== codigo;

  return (
    <div className={styles.card}>
      {/* Faixa de status */}
      <div className={`${styles.cardStatus} ${presentes3 ? styles.statusTres : somenteFisico ? styles.statusFisico : styles.statusEcomm}`}>
        {presentes3 ? '✦ 3 Canais' : somenteFisico ? '🏪 Só Físico' : '🛒 Só E-comm'}
      </div>

      {/* Imagem — tooltip ao passar o cursor */}
      <div
        className={styles.cardImage}
        onMouseEnter={onImageMouseEnter ? (e) => onImageMouseEnter(e, produto) : undefined}
        onMouseLeave={onImageMouseLeave}
        style={{ cursor: 'zoom-in' }}
      >
        <ProductImage
          src={imagemExibir}
          alt={nome}
          className={styles.img}
        />
      </div>

      {/* Códigos abaixo da imagem — clique para copiar */}
      {temDoisCodigos && (
        <div className={styles.dualCodes}>
          <button
            className={styles.dualCodeBadge}
            style={{ background: copiado === codigo ? '#16a34a' : '#2563eb' }}
            onClick={(e) => { e.stopPropagation(); copiarCodigo(codigo, setCopiado); }}
            title={`Copiar código ${codigo}`}
          >
            {copiado === codigo ? '✓ Copiado!' : `🏪 ${codigo}`}
          </button>
          <button
            className={styles.dualCodeBadge}
            style={{ background: copiado === codigoEcomm ? '#16a34a' : '#059669' }}
            onClick={(e) => { e.stopPropagation(); copiarCodigo(codigoEcomm, setCopiado); }}
            title={`Copiar código ${codigoEcomm}`}
          >
            {copiado === codigoEcomm ? '✓ Copiado!' : `🛒 ${codigoEcomm}`}
          </button>
        </div>
      )}

      {/* Info */}
      <div className={styles.cardBody}>
        {!temDoisCodigos && (
          <div
            className={`${styles.cardCodigo} ${styles.copiavel}`}
            onClick={(e) => { e.stopPropagation(); copiarCodigo(codigo, setCopiado); }}
            title={`Copiar código ${codigo}`}
          >
            {copiado === codigo ? '✓ Copiado!' : codigo}
          </div>
        )}
        <div className={styles.cardNome} title={nome}>{nome}</div>
        {fornecedor && <div className={styles.cardFornecedor}>{fornecedor}</div>}

        {/* Badges de presença */}
        <div className={styles.badges}>
          {presenca.map(p => {
            const b = BADGE_PRESENCA[p] || {};
            return b.label ? (
              <span key={p} className={styles.badge} style={{ background: b.bg }}>
                {b.icon} {b.label}
              </span>
            ) : null;
          })}
        </div>

        {/* Grid de dados por loja */}
        {(() => {
          const labelVendas = (lojaFisica.vendasFonte === 'periodo' || ecommerce.vendasFonte === 'periodo')
            ? 'Vendas no período'
            : 'Média/mês';
          return (
            <div className={styles.lojaGrid}>
              {/* Centro */}
              <div className={`${styles.lojaCol} ${styles.colCentro}`}>
                <div className={styles.lojaHeader}>🏪 Centro</div>
                <div className={styles.lojaMetric}>
                  <span className={styles.metrLabel}>Estoque</span>
                  <span
                    className={`${styles.metrVal} ${lojaFisica.estoqueCentro <= 0 ? styles.zero : ''}`}
                    title={lojaFisica.estoqueCentroNegativo ? 'Estoque negativo na fonte — possível erro de sync' : undefined}
                  >
                    {fmt(lojaFisica.estoqueCentro)}
                    {lojaFisica.estoqueCentroNegativo && <span className={styles.alertIcon}>⚠️</span>}
                  </span>
                </div>
                <div className={styles.lojaMetric}>
                  <span className={styles.metrLabel}>{labelVendas}</span>
                  <span className={styles.metrVal}>{fmt(lojaFisica.vendasMesCentro)}</span>
                </div>
              </div>

              {/* Caxias */}
              <div className={`${styles.lojaCol} ${styles.colCaxias}`}>
                <div className={styles.lojaHeader}>🏬 Caxias</div>
                <div className={styles.lojaMetric}>
                  <span className={styles.metrLabel}>Estoque</span>
                  <span
                    className={`${styles.metrVal} ${lojaFisica.estoqueCaxias <= 0 ? styles.zero : ''}`}
                    title={lojaFisica.estoqueCaxiasNegativo ? 'Estoque negativo na fonte — possível erro de sync' : undefined}
                  >
                    {fmt(lojaFisica.estoqueCaxias)}
                    {lojaFisica.estoqueCaxiasNegativo && <span className={styles.alertIcon}>⚠️</span>}
                  </span>
                </div>
                <div className={styles.lojaMetric}>
                  <span className={styles.metrLabel}>{labelVendas}</span>
                  <span className={styles.metrVal}>{fmt(lojaFisica.vendasMesCaxias)}</span>
                </div>
              </div>

              {/* E-commerce */}
              <div className={`${styles.lojaCol} ${styles.colEcomm}`}>
                <div className={styles.lojaHeader}>🛒 E-comm</div>
                <div className={styles.lojaMetric}>
                  <span className={styles.metrLabel}>Estoque</span>
                  <span
                    className={`${styles.metrVal} ${ecommerce.estoque <= 0 ? styles.zero : ''}`}
                    title={ecommerce.estoqueNegativo ? 'Estoque negativo na fonte — possível erro de sync' : undefined}
                  >
                    {fmt(ecommerce.estoque)}
                    {ecommerce.estoqueNegativo && <span className={styles.alertIcon}>⚠️</span>}
                  </span>
                </div>
                <div className={styles.lojaMetric}>
                  <span className={styles.metrLabel}>{labelVendas}</span>
                  <span className={styles.metrVal}>{fmt(ecommerce.vendasMes)}</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Footer */}
        <div className={styles.cardFooter}>
          <div className={styles.footerItem}>
            <span className={styles.footerLabel}>
              {(lojaFisica.vendasFonte === 'periodo' || ecommerce.vendasFonte === 'periodo') ? 'Total período' : 'Média/mês'}
            </span>
            <span className={styles.footerBig}>{fmt(vendasMesTotal)}</span>
          </div>
          <div className={styles.footerItem}>
            <span className={styles.footerLabel}>Estoque</span>
            <span className={styles.footerBig}>{fmt(estoqueTotal)}</span>
          </div>
          <div className={styles.footerItem}>
            <span className={styles.footerLabel}>Preço</span>
            <span className={styles.footerBig}>{fmtR$(preco)}</span>
          </div>
          <div className={styles.footerItem}>
            <span className={styles.footerLabel}>Markup</span>
            <span className={`${styles.footerBig} ${markup < 30 ? styles.markupBaixo : markup > 100 ? styles.markupAlto : ''}`}>
              {markup.toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Página Principal ────────────────────────────────────────────────────────
export default function ProdutosConsolidados() {
  const [page, setPage]             = useState(1);
  const [search, setSearch]         = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  const [categoria, setCategoria]   = useState('');
  const [sortField, setSortField]   = useState('');
  const [semEcommerce, setSemEcommerce] = useState(false);
  const [semFisica, setSemFisica]   = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim]       = useState('');

  // Busca em lote: lista de códigos aplicados como filtro
  const [showLoteModal, setShowLoteModal] = useState(false);
  const [codigosLoteText, setCodigosLoteText] = useState('');
  const [codigosLote, setCodigosLote] = useState([]);

  // ── Tooltip de imagem ──
  const [tooltipProduto, setTooltipProduto] = useState(null);
  const [tooltipPos, setTooltipPos]         = useState({ top: 0, left: 0, maxHeight: 0 });

  const handleImageMouseEnter = useCallback((event, produto) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth  = 340;
    const windowWidth   = window.innerWidth;
    const windowHeight  = window.innerHeight;
    const margin        = 12;

    // Posição horizontal: tenta a esquerda do card, senão vai para a direita
    let left = rect.left - tooltipWidth - 16;
    if (left < margin) left = rect.right + 16;
    if (left + tooltipWidth > windowWidth - margin)
      left = windowWidth - tooltipWidth - margin;

    const maxTooltipHeight = Math.floor(windowHeight * 0.88);
    let top = margin;
    if (rect.top < windowHeight / 2) {
      top = Math.max(margin, rect.top);
    } else {
      top = Math.max(margin, windowHeight - maxTooltipHeight - margin);
    }

    setTooltipPos({ top, left, maxHeight: maxTooltipHeight });
    setTooltipProduto(produto);
  }, []);

  const handleImageMouseLeave = useCallback(() => {
    setTooltipProduto(null);
  }, []);

  const searchTimeout = useRef(null);

  // Carregar filtros disponíveis (fornecedores + categorias) — uma única vez
  const { data: filtrosData } = useQuery({
    queryKey: ['consolidado-filtros'],
    queryFn: fetchFiltrosConsolidados,
    staleTime: 5 * 60_000,
  });
  const listFornecedores = filtrosData?.fornecedores || [];
  const listCategorias   = filtrosData?.categorias   || [];

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['produtos-consolidados', page, search, fornecedor, categoria, sortField, semEcommerce, semFisica, dataInicio, dataFim, codigosLote],
    queryFn: () => fetchProdutosConsolidados({
      page, search, fornecedor, categoria,
      sort: sortField,
      semEcommerce: semEcommerce ? 'true' : '',
      semFisica:    semFisica    ? 'true' : '',
      dataInicio, dataFim,
      codigosLote,
      limit: 200
    }),
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const aplicarLote = useCallback(() => {
    const codigos = codigosLoteText
      .split(/[\n,;]/)
      .map(c => c.trim())
      .filter(Boolean);
    setCodigosLote(codigos);
    setPage(1);
    setShowLoteModal(false);
  }, [codigosLoteText]);

  const limparLote = useCallback(() => {
    setCodigosLoteText('');
    setCodigosLote([]);
    setPage(1);
    setShowLoteModal(false);
  }, []);

  const handleSearchChange = useCallback((val) => {
    setInputSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  }, []);

  const handleSortChange = (val) => { setSortField(val); setPage(1); };
  const handlePageChange = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const metricas = data?.metricas || {};
  const produtos  = data?.data     || [];
  const paginacao = data?.pagination || {};

  const handleExportCSV = useCallback(() => {
    if (!produtos.length) return;
    const rows = [
      ['Código', 'Nome', 'Fornecedor', 'Categoria', 'Preço', 'Preço Custo',
       'Est.Centro', 'Est.Caxias', 'Est.Ecomm',
       'Vendas Centro', 'Vendas Caxias', 'Vendas Ecomm', 'Vendas Total'].join(';'),
      ...produtos.map(p => [
        p.codigo, p.nome, p.fornecedor || '', p.categoria || '',
        p.preco?.toFixed(2).replace('.', ',') || '0,00',
        p.precoCusto?.toFixed(2).replace('.', ',') || '0,00',
        p.lojaFisica.estoqueCentro, p.lojaFisica.estoqueCaxias, p.ecommerce.estoque,
        p.lojaFisica.vendasMesCentro, p.lojaFisica.vendasMesCaxias,
        p.ecommerce.vendasMes, p.vendasMesTotal,
      ].join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + rows], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `consolidado_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }, [produtos]);

  return (
    <div className={styles.page}>
      {/* ── Cabeçalho ── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <span className={styles.titleIcon}>⚡</span>
            Produtos Consolidados
          </h1>
          <p className={styles.subtitle}>Visão unificada das 3 lojas — Centro · Caxias · E-commerce</p>
        </div>
        <button className={styles.btnExport} onClick={handleExportCSV} title="Exportar CSV">
          📥 Exportar CSV
        </button>
      </div>


      {/* ── Filtros ── */}
      <div className={styles.filtros}>
        {/* Busca */}
        <div className={styles.filtroSearch}>
          <span className={styles.filtroSearchIcon}>🔍</span>
          <input
            className={styles.filtroInput}
            placeholder="Buscar por código ou nome..."
            value={inputSearch}
            onChange={e => handleSearchChange(e.target.value)}
            id="search-consolidado"
          />
          {inputSearch && (
            <button className={styles.clearBtn} onClick={() => { handleSearchChange(''); }}>✕</button>
          )}
        </div>

        {/* Fornecedor */}
        <select
          className={styles.filtroSelect}
          value={fornecedor}
          onChange={e => { setFornecedor(e.target.value); setPage(1); }}
          style={{ maxWidth: 220 }}
        >
          <option value="">Todos os fornecedores</option>
          {listFornecedores.map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>

        {/* Categoria */}
        <select
          className={styles.filtroSelect}
          value={categoria}
          onChange={e => { setCategoria(e.target.value); setPage(1); }}
          style={{ maxWidth: 200 }}
        >
          <option value="">Todas as categorias</option>
          {listCategorias.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        {/* Ordenação */}
        <select
          className={styles.filtroSelect}
          value={sortField}
          onChange={e => handleSortChange(e.target.value)}
        >
          {ORDENACAO_OPTS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* Período */}
        <div className={styles.periodoGroup}>
          <input type="date" className={styles.filtroInput} value={dataInicio}
            onChange={e => { setDataInicio(e.target.value); setPage(1); }} style={{ maxWidth: 150 }} />
          <span className={styles.periodoSep}>→</span>
          <input type="date" className={styles.filtroInput} value={dataFim}
            onChange={e => { setDataFim(e.target.value); setPage(1); }} style={{ maxWidth: 150 }} />
          {(dataInicio || dataFim) && (
            <button className={styles.clearBtn} onClick={() => { setDataInicio(''); setDataFim(''); setPage(1); }}>✕</button>
          )}
        </div>

        {/* Toggles de presença + busca em lote */}
        <div className={styles.togglesGroup}>
          <button
            className={`${styles.toggleBtn} ${semEcommerce ? styles.toggleActive : ''}`}
            onClick={() => { setSemEcommerce(v => !v); setSemFisica(false); setPage(1); }}
          >
            🏪 Só Físico
          </button>
          <button
            className={`${styles.toggleBtn} ${semFisica ? styles.toggleActive : ''}`}
            onClick={() => { setSemFisica(v => !v); setSemEcommerce(false); setPage(1); }}
          >
            🛒 Só E-comm
          </button>
          <button
            className={`${styles.toggleBtn} ${codigosLote.length ? styles.toggleActive : ''}`}
            onClick={() => setShowLoteModal(true)}
            title="Cole códigos múltiplos pra filtrar em lote"
          >
            📋 Lote{codigosLote.length ? ` (${codigosLote.length})` : ''}
          </button>
          {(semEcommerce || semFisica) && (
            <button className={styles.clearBtn} onClick={() => { setSemEcommerce(false); setSemFisica(false); }}>✕</button>
          )}
        </div>
      </div>

      {/* ── Status da busca ── */}
      {(search || fornecedor || categoria || semEcommerce || semFisica || dataInicio || codigosLote.length > 0) && (
        <div className={styles.statusBusca}>
          {paginacao.total !== undefined && (
            <span><strong>{fmt(paginacao.total)}</strong> produto(s) encontrado(s)</span>
          )}
          {codigosLote.length > 0 && (
            <span style={{ color: '#3b82f6', marginLeft: 12 }}>
              📋 Busca em lote: {codigosLote.length} código(s)
            </span>
          )}
          <button className={styles.linkClear} onClick={() => {
            setSearch(''); setInputSearch(''); setFornecedor(''); setCategoria('');
            setSemEcommerce(false); setSemFisica(false); setDataInicio(''); setDataFim('');
            setCodigosLoteText(''); setCodigosLote([]); setPage(1);
          }}>
            Limpar todos os filtros
          </button>
        </div>
      )}

      {/* ── Modal de Busca em Lote ── */}
      {showLoteModal && (
        <div
          onClick={() => setShowLoteModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 24, width: 'min(560px, 92vw)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <h2 style={{ margin: '0 0 12px', fontSize: '1.15rem', color: '#1e293b' }}>📋 Busca em Lote</h2>
            <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#64748b' }}>
              Cole os códigos (físico ou e-commerce) separados por linha, vírgula ou ponto-e-vírgula:
            </p>
            <textarea
              autoFocus
              rows={10}
              value={codigosLoteText}
              onChange={(e) => setCodigosLoteText(e.target.value)}
              placeholder="Ex:&#10;020934&#10;CP1283&#10;025775"
              style={{
                width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: '0.88rem',
                padding: 10, border: '1px solid #cbd5e1', borderRadius: 8, resize: 'vertical'
              }}
            />
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 6 }}>
              {codigosLoteText.split(/[\n,;]/).filter(c => c.trim()).length} código(s) detectado(s)
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              {codigosLote.length > 0 && (
                <button
                  onClick={limparLote}
                  style={{ background: '#fff', color: '#dc2626', border: '1px solid #dc2626', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                >
                  Limpar Lote
                </button>
              )}
              <button
                onClick={() => setShowLoteModal(false)}
                style={{ background: '#fff', color: '#475569', border: '1px solid #cbd5e1', padding: '8px 14px', borderRadius: 6, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={aplicarLote}
                style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {isLoading && <Loading />}

      {/* ── Erro ── */}
      {!isLoading && isError && (
        <div className={styles.erro}>
          ❌ Erro ao carregar: {error?.message || 'Erro desconhecido'}
        </div>
      )}

      {/* ── Vazio ── */}
      {!isLoading && !isError && produtos.length === 0 && (
        <div className={styles.vazio}>
          <div className={styles.vazioIcon}>🔍</div>
          <div className={styles.vazioMsg}>Nenhum produto encontrado</div>
          <div className={styles.vazioSub}>Tente ajustar os filtros</div>
        </div>
      )}

      {/* ── Grade de produtos ── */}
      {!isLoading && !isError && produtos.length > 0 && (
        <>
          <div className={styles.grid}>
            {produtos.map(p => (
              <ProdutoCard
                key={p.codigo}
                produto={p}
                onImageMouseEnter={handleImageMouseEnter}
                onImageMouseLeave={handleImageMouseLeave}
              />
            ))}
          </div>

          {/* Paginação */}
          <div className={styles.paginacao}>
            <Pagination
              currentPage={paginacao.page || 1}
              totalPages={paginacao.totalPages || 1}
              onPageChange={handlePageChange}
            />
            <div className={styles.paginacaoInfo}>
              Página {paginacao.page} de {paginacao.totalPages} —{' '}
              {fmt(paginacao.total)} produtos
            </div>
          </div>
        </>
      )}

      {/* ── Tooltip de Imagem Grande ── */}
      {tooltipProduto && (
        <div
          className={tooltipStyles.tooltip}
          style={{
            top:       `${tooltipPos.top}px`,
            left:      `${tooltipPos.left}px`,
            maxHeight: tooltipPos.maxHeight ? `${tooltipPos.maxHeight}px` : '88vh',
            display:   'block',
            zIndex:    99999,
          }}
        >
          {/* Imagem grande — prioriza e-commerce quando está nos 2 canais */}
          <div className={tooltipStyles.tooltipImageContainer}>
            <ProductImage
              src={
                tooltipProduto.lojaFisica?.presente && tooltipProduto.ecommerce?.presente && tooltipProduto.ecommerce.codigoEcommerce
                  ? `/api/produtos/imagem/${tooltipProduto.ecommerce.codigoEcommerce}`
                  : `/api/produtos/imagem/${tooltipProduto.codigo}`
              }
              alt={tooltipProduto.nome}
              className={tooltipStyles.tooltipMainImage}
            />
          </div>

          {/* Nome */}
          <div className={tooltipStyles.tooltipHeader}>{tooltipProduto.nome}</div>

          <div className={tooltipStyles.tooltipContent}>
            {/* Código(s) */}
            <div className={tooltipStyles.tooltipRow}>
              <span className={tooltipStyles.tooltipLabel}>🏪 Cód. Físico</span>
              <span className={tooltipStyles.tooltipValue}>{tooltipProduto.codigo}</span>
            </div>
            {tooltipProduto.ecommerce?.codigoEcommerce && tooltipProduto.ecommerce.codigoEcommerce !== tooltipProduto.codigo && (
              <div className={`${tooltipStyles.tooltipRow} ${tooltipStyles.rowStriped}`}>
                <span className={tooltipStyles.tooltipLabel}>🛒 Cód. E-comm</span>
                <span className={tooltipStyles.tooltipValue}>{tooltipProduto.ecommerce.codigoEcommerce}</span>
              </div>
            )}
            {tooltipProduto.fornecedor && (
              <div className={tooltipStyles.tooltipRow}>
                <span className={tooltipStyles.tooltipLabel}>Fornecedor</span>
                <span className={tooltipStyles.tooltipValue}>{tooltipProduto.fornecedor}</span>
              </div>
            )}
            {tooltipProduto.categoria && (
              <div className={`${tooltipStyles.tooltipRow} ${tooltipStyles.rowStriped}`}>
                <span className={tooltipStyles.tooltipLabel}>Categoria</span>
                <span className={tooltipStyles.tooltipValue}>{tooltipProduto.categoria}</span>
              </div>
            )}

            <div className={tooltipStyles.tooltipDivider} />

            {/* Preço e markup */}
            <div className={`${tooltipStyles.tooltipRow} ${tooltipStyles.rowStriped}`}>
              <span className={tooltipStyles.tooltipLabel}>Preço Venda</span>
              <span className={tooltipStyles.tooltipValue}>
                {fmtR$(tooltipProduto.preco)}
              </span>
            </div>
            {tooltipProduto.precoCusto > 0 && (
              <div className={tooltipStyles.tooltipRow}>
                <span className={tooltipStyles.tooltipLabel}>Markup</span>
                <span className={tooltipStyles.tooltipValue}
                  style={{
                    color: tooltipProduto.precoCusto > 0
                      ? ((tooltipProduto.preco - tooltipProduto.precoCusto) / tooltipProduto.precoCusto * 100) < 30
                        ? '#ef4444'
                        : '#059669'
                      : '#1e293b'
                  }}
                >
                  {tooltipProduto.precoCusto > 0
                    ? ((tooltipProduto.preco - tooltipProduto.precoCusto) / tooltipProduto.precoCusto * 100).toFixed(0) + '%'
                    : '—'}
                </span>
              </div>
            )}

            <div className={tooltipStyles.tooltipDivider} />

            {/* Estoque por loja */}
            <div className={tooltipStyles.tooltipSubHeader}>Estoques</div>
            <div className={tooltipStyles.tooltipRow}>
              <span className={tooltipStyles.tooltipLabel}>🏪 Centro</span>
              <span className={tooltipStyles.tooltipValue}
                style={{ color: tooltipProduto.lojaFisica.estoqueCentro <= 0 ? '#d1d5db' : '#1e293b' }}
              >
                {fmt(tooltipProduto.lojaFisica.estoqueCentro)}
              </span>
            </div>
            <div className={`${tooltipStyles.tooltipRow} ${tooltipStyles.rowStriped}`}>
              <span className={tooltipStyles.tooltipLabel}>🏬 Caxias</span>
              <span className={tooltipStyles.tooltipValue}
                style={{ color: tooltipProduto.lojaFisica.estoqueCaxias <= 0 ? '#d1d5db' : '#1e293b' }}
              >
                {fmt(tooltipProduto.lojaFisica.estoqueCaxias)}
              </span>
            </div>
            <div className={tooltipStyles.tooltipRow}>
              <span className={tooltipStyles.tooltipLabel}>🛒 E-commerce</span>
              <span className={tooltipStyles.tooltipValue}
                style={{ color: tooltipProduto.ecommerce.estoque <= 0 ? '#d1d5db' : '#1e293b' }}
              >
                {fmt(tooltipProduto.ecommerce.estoque)}
              </span>
            </div>

            <div className={tooltipStyles.tooltipDivider} />

            {/* Vendas / Média mensal */}
            {(() => {
              const isPeriodo = tooltipProduto.lojaFisica.vendasFonte === 'periodo' || tooltipProduto.ecommerce.vendasFonte === 'periodo';
              const titulo = isPeriodo ? 'Vendas no Período' : 'Média Mensal (histórica)';
              return <div className={tooltipStyles.tooltipSubHeader}>{titulo}</div>;
            })()}
            <div className={tooltipStyles.tooltipRow}>
              <span className={tooltipStyles.tooltipLabel}>🏪 Centro</span>
              <span className={tooltipStyles.tooltipValue}>{fmt(tooltipProduto.lojaFisica.vendasMesCentro)}</span>
            </div>
            <div className={`${tooltipStyles.tooltipRow} ${tooltipStyles.rowStriped}`}>
              <span className={tooltipStyles.tooltipLabel}>🏬 Caxias</span>
              <span className={tooltipStyles.tooltipValue}>{fmt(tooltipProduto.lojaFisica.vendasMesCaxias)}</span>
            </div>
            <div className={tooltipStyles.tooltipRow}>
              <span className={tooltipStyles.tooltipLabel}>🛒 E-commerce</span>
              <span className={tooltipStyles.tooltipValue}>{fmt(tooltipProduto.ecommerce.vendasMes)}</span>
            </div>
            <div className={`${tooltipStyles.tooltipRow} ${tooltipStyles.rowStriped}`}>
              <span className={tooltipStyles.tooltipLabel} style={{ fontWeight: 700, color: '#1e293b' }}>Total</span>
              <span className={tooltipStyles.tooltipValue} style={{ fontWeight: 800, color: '#6366f1' }}>
                {fmt(tooltipProduto.vendasMesTotal)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
